import { nanoid } from 'nanoid';
import { useCallback, useMemo } from 'react';
import { DataSelect, DataSelectInheritedProps } from 'components/selects/DataSelect';
import { useApi } from 'api/api-context';
import { FEATURE_TOGGLE_REVISION_TECHNICIAN_SELECT_GROUP_BY_ORGANIZATION } from 'env';

const EMPTY_TECHNICIAN_IDS: number[] = [];

interface RevisionTechnicianSelectFilters {
  kind: string;
  departmentId: number;
  deviceTypeId: number;
  excludeTechnicianIds?: number[];
  allowNoCertificate?: boolean;
}

/**
 * Fetches revision technicians from API.
 */
function useRevisionTechnicianSelect({
  kind,
  departmentId,
  deviceTypeId,
  excludeTechnicianIds = EMPTY_TECHNICIAN_IDS,
  allowNoCertificate = false,
}: RevisionTechnicianSelectFilters) {
  const { getAction } = useApi();
  const excludeTechnicianIdsKey = excludeTechnicianIds.join(',');
  const excludedTechnicianIds = useMemo(() => new Set(excludeTechnicianIds), [excludeTechnicianIdsKey]);

  const fetchRevisionTechnicians = useCallback(async () => {
    const action = getAction('RevisionListTechnicians');

    const query: Record<string, any> = {
      kind,
      departmentId: String(departmentId),
      deviceTypeId: String(deviceTypeId),
    };

    if (allowNoCertificate) {
      query.allowNoCertificate = 1;
    }

    const technicians = await action({ query });

    return technicians
      .filter(({ userId }) => !excludedTechnicianIds.has(userId))
      .map(({ fullName, userId, organization }) => ({
        label: `${fullName}`,
        value: String(userId),
        group: FEATURE_TOGGLE_REVISION_TECHNICIAN_SELECT_GROUP_BY_ORGANIZATION ? organization.name : '',
      }))
      .sort((a, b) => {
        if (a.group < b.group) {
          return -1;
        }

        if (a.group > b.group) {
          return 1;
        }

        return a.label.localeCompare(b.label);
      });
  }, [getAction, kind, departmentId, deviceTypeId, excludedTechnicianIds, allowNoCertificate]);

  return { fetchRevisionTechnicians };
}

/**
 * Parameters of the RevisionTechnicianSelect component.
 */
type RevisionTechnicianSelectProps = DataSelectInheritedProps & RevisionTechnicianSelectFilters;

/**
 * Select for revision technicians.
 */
export function RevisionTechnicianSelect({
  kind,
  departmentId,
  deviceTypeId,
  excludeTechnicianIds = EMPTY_TECHNICIAN_IDS,
  allowNoCertificate,
  ...props
}: RevisionTechnicianSelectProps) {
  const excludeTechnicianIdsKey = excludeTechnicianIds.join(',');
  const key = useMemo(nanoid, [kind, departmentId, deviceTypeId, excludeTechnicianIdsKey, allowNoCertificate]);

  const { fetchRevisionTechnicians } = useRevisionTechnicianSelect({
    kind,
    departmentId,
    deviceTypeId,
    excludeTechnicianIds,
    allowNoCertificate,
  });

  return (
    <DataSelect
      key={key}
      valueProp="value"
      labelProp="label"
      groupProp="group"
      action={fetchRevisionTechnicians}
      {...props}
    />
  );
}
